import React, {useState} from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  InputBase,
  Grid,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { 
  IMG_SWEATSHIRT_BLACK, 
  IMG_DAINASOUL_TSHIRT1, 
  IMG_HOODIE_PINK_3, 
  IMG_DAINASOUL_MAIN_IMAGE, 
  IMG_SILVER_NECKLACE, 
  IMG_DAINASOUL_HOODIE_BLACK_2,
  IMG_HOODIE_BLUE_2,
  IMG_HOODIE_WHITE_1,
  IMG_DAINASOUL_HOODIE_SMALL_LOGO,
  IMG_HOODIE_BEIGE_RED_1,
  IMG_DAINASOUL_STORE,
  IMG_SWEATSHIRT_BLACK_MAIN,
  IMG_TSHIRT_BEIGE_LOGO_RED,
  IMG_TSHIRT_BEIGE_LOGO_BLACK_GIRL,
  IMG_DAINASOUL_CAP_CREAM,
  IMG_DAINASOUL_CANVAS_TOTE_BLACK,
} from './imageConfig';
import { tshirtProducts } from './Products';

const SearchDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const getTag = (name) => {
    const n = name.toLowerCase();
    if (n.includes('sweatshirt') || n.includes('hoodie')) return 'Coming in Winter';
    if (n.includes('shorts') || n.includes('bag') || n.includes('cap') || n.includes('accessor') || n.includes('edit') || n.includes('essentials') || n.includes('sets')) return 'Coming Soon';
    return null;
  };

  const handleCategoryClick = (slug, name) => {
    // We now allow clicking even if there's a tag, so the user can see the "Coming Soon" page in CategoryPage.js
    navigate(`/products/${slug}`);
    onClose();
  };

  const handleSearchClick = (term) => {
    navigate(`/search?q=${encodeURIComponent(term)}&resources[type]=product`);
    onClose();
  };

  const handleProductClick = (product) => {
    if (product.category === 'hoodie' || product.category === 'sweatshirt') return;
    const slug = product.title
      .toLowerCase()
      .replace(/\s*\|\s*/g, "-")
      .replace(/\s+/g, "-");

    navigate(`/product-description/${slug}`, {
      state: {
        product: product,
        country: null, // We'll handle country inside ProductDescription if null
      },
    });
    onClose();
  };

  const categories = [
    { title: 'DAINASOUL CLASSICS', img: IMG_TSHIRT_BEIGE_LOGO_BLACK_GIRL, slug: 'timeless-classics' },
    { title: 'T-SHIRTS & TOPS', img: IMG_TSHIRT_BEIGE_LOGO_RED, slug: 't-shirts & tops' },
    { title: 'SWEATSHIRTS', img: IMG_SWEATSHIRT_BLACK_MAIN, slug: 'sweatshirts' },
    { title: 'CAPS', img: IMG_DAINASOUL_CAP_CREAM, slug: 'caps' },
    { title: 'ACCESSORIES', img: IMG_DAINASOUL_CANVAS_TOTE_BLACK, slug: 'accessories' },
  ];

  const getSearchData = (query) => {
    const q = query.toLowerCase();

    // Default empty state
    let data = {
      suggestions: [],
      collections: [],
      products: []
    };

    if (q.includes('shirt') || q.includes('top')) {
      data = {
        suggestions: ['t shirt', 'oversized t shirt', 'white t shirt', 'tops', 'graphic tees'],
        collections: [{ label: 'T-Shirts & Tops', slug: 't-shirts & tops' }],
        products: tshirtProducts
      };
    } else if (q.includes('classic') || q.includes('timeless')) {
      data = {
        suggestions: ['classics', 'timeless essentials', 'core collection'],
        collections: [{ label: 'DAINASOUL Classics', slug: 'timeless-classics' }],
        products: [
          { 
            id: 201,
            title: 'DAINASOUL Signature Sweatshirt', 
            prices: { default: '$129.00', US: '$129.00', IN: '₹5,999.00' }, 
            images: [IMG_SWEATSHIRT_BLACK], 
            slug: 'timeless-sweatshirt',
            category: 'hoodie' 
          },
        ]
      };
    } else {
      // For all other queries (hoodies, sweatshirts, caps, etc.), provide suggestions/collections but NO products
      if (q.includes('hoodie')) {
        data = {
          suggestions: ['hoodie', 'vogue hoodie', 'hoodie blau'],
          collections: [{ label: 'Hoodies', slug: 'hoodies' }],
          products: []
        };
      } else if (q.includes('sweat')) {
        data = {
          suggestions: ['sweatshirt', 'black sweatshirt'],
          collections: [{ label: 'Sweatshirts', slug: 'sweatshirts' }],
          products: []
        };
      } else if (q.includes('access')) {
        data = {
          suggestions: ['silver necklace', 'earrings'],
          collections: [{ label: 'Accessories', slug: 'accessories' }],
          products: []
        };
      } else if (q.includes('cap')) {
        data = {
          suggestions: ['caps', 'baseball caps'],
          collections: [{ label: 'Caps', slug: 'caps' }],
          products: []
        };
      } else if (q.includes('bag')) {
        data = {
          suggestions: ['tote bag', 'canvas tote'],
          collections: [{ label: 'Bags', slug: 'bags' }],
          products: []
        };
      }
    }

    return data;
  };

  const searchData = getSearchData(searchQuery);

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          backgroundColor: '#fff',
          color: '#000',
        }
      }}
      sx={{ zIndex: 1302 }}
    >
      <Box sx={{ px: { xs: 2, md: 8 }, py: { xs: 2, md: 4 }, boxSizing: 'border-box', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SearchIcon sx={{ color: '#000', mr: 2, fontSize: '28px' }} />
          <InputBase 
            placeholder="Start typing to search" 
            fullWidth
            sx={{ color: '#000', fontSize: '20px', fontWeight: 300 }}
            autoFocus
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(`/search?q=${encodeURIComponent(searchQuery)}&resources[type]=product`);
                onClose();
              }
            }}
          />
          <IconButton onClick={onClose} sx={{ color: '#000', ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {searchQuery.trim() === '' ? (
          <>
            <Grid container spacing={4} sx={{ mb: 6 }}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Popular searches</Typography>
                <Box sx={{ display: 'flex', gap: 3, color: "#655f5fff" }}>
                  {[
                    { label: 'Sweatshirts', slug: 'sweatshirts' },
                    { label: 'Shorts', slug: 'shorts' },
                    { label: 'Caps', slug: 'caps' },
                  ].map(item => {
                    const tag = getTag(item.label);
                    return (
                      <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          onClick={() => handleCategoryClick(item.slug, item.label)}
                          sx={{ 
                            cursor: tag ? 'default' : 'pointer', 
                            fontSize: '14px', 
                            color: tag ? '#999' : 'inherit',
                            '&:hover': { textDecoration: tag ? 'none' : 'underline' } 
                          }}
                        >
                          {item.label}
                        </Typography>
                        {tag && (
                          <Typography sx={{ fontSize: '10px', color: '#888', fontStyle: 'italic' }}>
                            ({tag})
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Useful pages</Typography>
                <Box sx={{ display: 'flex', gap: 3, color: "#655f5fff" }}>
                  {[
                    { label: 'FAQs', slug: 'faq' },
                    { label: 'Returns', slug: 'return-policy' },
                    { label: 'Shipping', slug: 'contact' },
                  ].map(item => (
                    <Typography
                      key={item.label}
                      onClick={() => { navigate(`/${item.slug}`); onClose(); }}
                      sx={{ cursor: 'pointer', fontSize: '14px', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {item.label}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, mx: 0 }}>
              {categories.map((cat) => {
                const tag = getTag(cat.title);
                return (
                  <Box
                    key={cat.title}
                    sx={{ width: { xs: '33.33%', md: '20%' }, px: { xs: 0.5, md: 0 }, mb: { xs: 2, md: 0 }, cursor: tag ? 'default' : 'pointer' }}
                    onClick={() => handleCategoryClick(cat.slug, cat.title)}
                  >
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <Box 
                        component="img" 
                        src={cat.img} 
                        alt={cat.title} 
                        sx={{ 
                          width: '100%', 
                          aspectRatio: '3/4', 
                          objectFit: 'cover', 
                          mb: 1, 
                          display: 'block',
                          opacity: tag ? 0.6 : 1
                        }} 
                      />
                      {tag && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            padding: '2px 6px',
                            borderRadius: '2px',
                            backdropFilter: 'blur(4px)'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '8px',
                              fontWeight: 700,
                              color: '#000',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {tag.toUpperCase()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ 
                      fontSize: '11px', 
                      letterSpacing: '1px', 
                      fontWeight: 600, 
                      mt: 2, 
                      textAlign: 'left', 
                      color: tag ? '#888' : '#000',
                      '&:hover': { textDecoration: tag ? 'none' : 'underline' } 
                    }}>
                      {cat.title}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Search suggestions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, color: "#000", mb: 4 }}>
                {searchData.suggestions.map((suggestion, index) => (
                  <Typography key={index} onClick={() => handleSearchClick(suggestion)} sx={{ fontSize: '13px', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    {suggestion}
                  </Typography>
                ))}
              </Box>

              <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Collection results ({searchData.collections.length})</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, color: "#000" }}>
                {searchData.collections.map((collection, index) => (
                  <Typography key={index} onClick={() => handleCategoryClick(collection.slug)} sx={{ fontSize: '13px', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    {collection.label}
                  </Typography>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>Products ({searchData.products.length} of {searchData.products.length < 30 ? searchData.products.length : 30})</Typography>
                <Typography onClick={() => { navigate(`/search?q=${encodeURIComponent(searchQuery)}&resources[type]=product`); onClose(); }} sx={{ fontWeight: 600, fontSize: '12px', letterSpacing: '1px', cursor: 'pointer' }}>VIEW ALL RESULTS</Typography>
              </Box>
              <Grid container spacing={1} sx={{ mx: -0.5 }}>
                {searchData.products.map((prod, idx) => (
                  <Grid item xs={6} md={2} key={idx} sx={{ px: 0.5 }}>
                    <Box 
                      onClick={() => handleProductClick(prod)}
                      sx={{ 
                        cursor: (prod.category === 'hoodie' || prod.category === 'sweatshirt') ? 'default' : 'pointer', 
                        '&:hover img': { opacity: (prod.category === 'hoodie' || prod.category === 'sweatshirt') ? 1 : 0.9 } 
                      }}
                    >
                      <Box sx={{ position: 'relative', pt: '125%', bgcolor: '#f4f4f4', mb: 1.5 }}>
                        <Box 
                          component="img" 
                          src={prod.img} 
                          sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            transition: 'opacity 0.2s'
                          }} 
                        />
                      </Box>
                      <Typography sx={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        mb: 0.5, 
                        lineHeight: 1.3,
                        '&:hover': { textDecoration: 'underline' }
                      }}>
                        {prod.title?.replace(/\|/g, "")}
                      </Typography>
                    </Box>
                    {prod.oldPrice ? (
                      <Box sx={{ display: 'flex', gap: 1, fontSize: '11px' }}>
                        <Typography sx={{ color: 'red', fontWeight: 600, fontSize: '11px' }}>{prod.price}</Typography>
                        <Typography sx={{ textDecoration: 'line-through', color: '#888', fontSize: '11px' }}>{prod.oldPrice}</Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '11px', color: '#000' }}>{prod.price}</Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

export default SearchDrawer;
